/**
 * Project line items — the install manifest (EX.4).
 *
 * The standardized product list extracted from intake documents: sales-order
 * items (commercial view), packing-slip components (what physically ships —
 * the crew's receiving list), and design-package schedules (new/reused/
 * existing). Persisting them turns the discarded extraction into a real
 * manifest the crew can check off, and lets us flag back-orders (Ordered ≠
 * Shipped) that would block an install.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
export const LineItemSourceSchema = z.enum([
    'sales_order',
    'packing_slip',
    'design_new',
    'design_reused',
    'design_existing',
]);
export const ProjectLineItemSchema = z.object({
    id: UuidSchema,
    project_id: UuidSchema,
    source: LineItemSourceSchema,
    line_number: z.number().int().nullable(),
    product_code: z.string().nullable().describe('SKU / model number'),
    description: z.string(),
    quantity: z.number().int().nullable().describe('ordered quantity'),
    backordered: z.number().int().nullable().describe('packing slip only'),
    shipped: z.number().int().nullable().describe('packing slip only'),
    unit_price_cents: z.number().int().nullable().describe('sales order only'),
    total_cents: z.number().int().nullable().describe('sales order only'),
    received: z.boolean(),
    installed: z.boolean(),
    /** Derived: ordered but not fully shipped — blocks install completion. */
    is_backordered: z.boolean(),
    created_at: IsoDateTimeSchema,
});
export const ProjectLineItemCreateSchema = z.object({
    source: LineItemSourceSchema,
    line_number: z.number().int().nullish(),
    product_code: z.string().max(100).nullish(),
    description: z.string().min(1).max(500),
    quantity: z.number().int().nullish(),
    backordered: z.number().int().nullish(),
    shipped: z.number().int().nullish(),
    unit_price_cents: z.number().int().nullish(),
    total_cents: z.number().int().nullish(),
});
/** Bulk insert (the intake flow posts a project's whole manifest at once). */
export const ProjectLineItemsBulkSchema = z.object({
    items: z.array(ProjectLineItemCreateSchema).max(300),
});
/** Crew check-off — toggle received / installed. */
export const ProjectLineItemUpdateSchema = z
    .object({
    received: z.boolean().optional(),
    installed: z.boolean().optional(),
})
    .refine((v) => v.received !== undefined || v.installed !== undefined, {
    message: 'Provide received and/or installed',
});
export const ProjectLineItemListSchema = z.object({
    data: z.array(ProjectLineItemSchema),
});
//# sourceMappingURL=line-items.js.map